import React, { useState } from 'react'
import { Button, Column, Columns, Container } from '@resurs/midas-react'
import { useLocale } from '@merchant-portal/merchant-portal-base'
import moment from 'moment/moment'
import PropTypes from 'prop-types'
import {
  faExclamationCircle,
  faExclamationTriangle,
  faPen,
} from '@fortawesome/free-solid-svg-icons'
import Page from '../../../internal/Page'
import ConfirmDialog from '../../../components/ConfirmDialog'
import PageHeading from '../../../components/common/PageHeading/PageHeading'
import { getText } from '../../../internal/TextLabels'
import PageActionBar from '../../../internal/PageActionBar'
import ContactInfo from '../../Transactions/TransactionOrderInfo/ContactInfo'
import AddressInfo from '../../Transactions/TransactionOrderInfo/AddressInfo'
import govIdFormat from '../../../util/govIdFormat'
import { Countries } from '../../../models/countries'
import { FormattedAmount } from '../../Transactions/shared/transaction-utils'
import { prettyPrintDate } from '../../../util/dateUtils'
import { TransactionStatus } from '../../../util/transactionConstants'
import { debitDateHasPassed } from '../ongoingPaymentUtils'
import DatepickerModal from '../../../components/common/DatepickerModal'
import DefaultEmptyInfoBox from './DefaultEmptyInfoBox'
import Banner from '../../../components/CommonUI/Banner/Banner'
import resend from '../../../assets/img/resend.svg'
import OngoingPaymentsResendModal from '../OngoingPaymentsResendSigningLinkModal'

import ongoingStyles from './OngoingPaymentDetails.module.scss'
import styles from '../../Transactions/shared/transactions.style'

const OngoingPaymentDetailsUI = ({
  authorizationTimeInDays = 90,
  updateAutomaticDebitDate,
  ongoingPayment,
  isDebitable,
  isProcessingDebit,
  isAnnulable,
  isProcessingAnnul,
  onDebit,
  onAnnul,
}) => {
  const [locale] = useLocale()
  const {
    account,
    application,
    channelType,
    customer,
    externalId,
    id,
    payment: {
      paymentStatus,
      settings: { automaticDebitDate, delayedPayment } = {},
    } = {},
    paymentMethod,
    publicSettings: { automaticDebit, pushedOutToCustomer },
    store,
    status,
    tags,
  } = ongoingPayment
  const maxDate = moment()
    .startOf('day')
    .add(authorizationTimeInDays, 'day')
    .toDate()
  const minDate = moment().add(1, 'day').startOf('day').toDate()
  const [showAutomaticDebitDateModal, setShowAutomaticDebitDateModal] =
    useState(false)
  const [updatedDebitDate, setUpdatedDebitDate] = useState(
    moment(automaticDebitDate).toDate(),
  )
  const [showAnnulConfirmDialog, setShowAnnulConfigDialog] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [signingLinkInfo, setSigningLinkInfo] = useState({
    email: customer?.email,
    mobile: customer?.mobile,
    valid: false,
  })
  const DEBIT_FAILED_TAGS = ['FAILED_TO_DEBIT', 'DEBIT_TIMEOUT']
  const debitFailed = tags.some(tag => DEBIT_FAILED_TAGS.includes(tag.type))

  const validChange = () =>
    automaticDebitDate &&
    updatedDebitDate &&
    !moment(updatedDebitDate).isBefore(minDate) &&
    !moment(updatedDebitDate).isAfter(maxDate)

  const handleCloseModal = () => {
    setShowAutomaticDebitDateModal(false)
  }

  const handleUpdateDebitDate = () => {
    if (validChange) {
      updateAutomaticDebitDate(
        id,
        moment(updatedDebitDate).format('YYYY-MM-DD'),
      )
    }
    handleCloseModal()
  }

  return (
    <Container>
      <Column>
        <PageHeading title={getText('ongoingPayments.details.title')} />
        {status === TransactionStatus.SIGNING_REQUIRED &&
          debitDateHasPassed(automaticDebitDate) && (
            <Banner
              faIcon={faExclamationTriangle}
              type="warning"
              text={getText(
                'ongoingPayments.details.notSignedAndDateHasPassed',
              )}
            />
          )}
        {debitFailed && (
          <Banner
            faIcon={faExclamationCircle}
            type="danger"
            text={getText('ongoingPayments.details.debitFailed')}
          />
        )}
      </Column>

      <Page>
        <PageActionBar
          items={
            <h6>
              <span style={styles.grayNameLabel}>
                {`${getText('ongoingPayments.details.customer')}: `}
              </span>
              <span style={{ fontWeight: 100 }}>
                {`(${govIdFormat(customer.governmentId, locale.country)})`}
              </span>
            </h6>
          }
        />
        <Container>
          <Columns>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.accountNumber"
                value={account?.accountNumber}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.customerType"
                value={getText(
                  `ongoingPayments.details.customerTypeValue.${customer.customerType}`,
                )}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
            <Column col2 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.channelType"
                value={getText(
                  `ongoingPayments.details.channelTypeValue.${channelType}`,
                )}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
            <Column col2 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.headers.reference"
                value={externalId}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
            <Column col2 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.headers.method"
                value={paymentMethod.description}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
          </Columns>
        </Container>
        <Container>
          <Columns>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.headers.status"
                value={
                  debitFailed
                    ? getText('ongoingPayments.status.failed')
                    : getText(`ongoingPayments.details.paymentStatus.${status}`)
                }
                additionalStyles={[
                  styles.noPaddingLeft,
                  styles.flexRowSpaceBetween,
                ]}
                svgIcon={
                  status === TransactionStatus.SIGNING_REQUIRED &&
                  !automaticDebit &&
                  delayedPayment &&
                  resend
                }
                svgIconAltText={getText('ongoingPayments.resend')}
                onIconClick={() => {
                  setShowModal(true)
                }}
              />
            </Column>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.debitDate"
                value={
                  pushedOutToCustomer && automaticDebit
                    ? getText('ongoingPayments.whenSigned')
                    : prettyPrintDate(automaticDebitDate)
                }
                valueStrikeThrough={debitDateHasPassed(automaticDebitDate)}
                additionalStyles={[
                  styles.noPaddingLeft,
                  styles.flexRowSpaceBetween,
                ]}
                faIcon={automaticDebitDate ? faPen : undefined}
                onIconClick={() => setShowAutomaticDebitDateModal(true)}
              />
            </Column>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.sum"
                value={FormattedAmount(paymentStatus.orderAmount)}
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
            <Column col3 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.requestedExtra"
                value={
                  application &&
                  paymentStatus &&
                  FormattedAmount(
                    application.approvedAmount - paymentStatus.orderAmount,
                  )
                }
                additionalStyles={[styles.noPaddingLeft]}
              />
            </Column>
          </Columns>
        </Container>
        <Container>
          <Columns>
            <Column col4 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.billingAddress"
                value={
                  <AddressInfo
                    address={
                      store.country !== Countries.NO
                        ? customer.billingAddress ||
                          customer.customerGivenAddress
                        : customer.customerGivenAddress
                    }
                    customer={customer}
                  />
                }
                additionalStyles={[
                  styles.noBorderBottom,
                  styles.noPaddingLeft,
                  styles.borderLeft,
                ]}
              />
            </Column>
            <Column col4 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.deliveryAddress"
                value={
                  <AddressInfo
                    address={customer.customerGivenDeliveryAddress}
                  />
                }
                additionalStyles={[
                  styles.noBorderBottom,
                  styles.noPaddingLeft,
                  styles.borderLeft,
                ]}
              />
            </Column>
            <Column col4 noPadding>
              <DefaultEmptyInfoBox
                label="ongoingPayments.details.contactInfo"
                value={<ContactInfo customer={customer} />}
                additionalStyles={[
                  styles.noBorderBottom,
                  styles.noPaddingLeft,
                  styles.borderLeft,
                ]}
              />
            </Column>
          </Columns>
        </Container>
        <DatepickerModal
          show={showAutomaticDebitDateModal}
          onClose={handleCloseModal}
          title={getText('ongoingPayments.datepickerModal.title')}
          description={getText(
            'ongoingPayments.datepickerModal.description',
            prettyPrintDate(minDate),
            prettyPrintDate(maxDate),
          )}
          selectDate={setUpdatedDebitDate}
          selectedDate={updatedDebitDate}
          onSave={handleUpdateDebitDate}
          language={locale.language}
          minDate={minDate}
          maxDate={maxDate}
        />
      </Page>

      <Page>
        <div className={ongoingStyles.actionBar}>
          <Button
            id="debit-button"
            type="primary"
            kind="normal"
            disabled={!isDebitable || isProcessingDebit}
            loading={isProcessingDebit}
            onClick={onDebit}
          >
            {getText('ongoingPayments.details.action.debit')}
          </Button>
          <Button
            id="annul-button"
            type="normal"
            kind="danger"
            disabled={!isAnnulable || isProcessingAnnul}
            loading={isProcessingAnnul}
            onClick={() => setShowAnnulConfigDialog(true)}
          >
            {getText('ongoingPayments.details.action.annul')}
          </Button>
        </div>

        <ConfirmDialog
          show={showAnnulConfirmDialog}
          title={getText('ongoingPayments.details.confirmAnnul.title')}
          text={getText('ongoingPayments.details.confirmAnnul.text')}
          action={getText('ongoingPayments.details.confirmAnnul.action')}
          onClose={() => setShowAnnulConfigDialog(false)}
          onConfirm={() => {
            setShowAnnulConfigDialog(false)
            onAnnul()
          }}
          destructive
        />
      </Page>
      {showModal && (
        <OngoingPaymentsResendModal
          selectedTransaction={{
            date: ongoingPayment.created,
            ...ongoingPayment,
          }}
          signingLinkInfo={signingLinkInfo}
          setSigningLinkInfo={setSigningLinkInfo}
          setShowModal={setShowModal}
        />
      )}
    </Container>
  )
}

OngoingPaymentDetailsUI.propTypes = {
  ongoingPayment: PropTypes.object.isRequired,
  isDebitable: PropTypes.bool.isRequired,
  isProcessingDebit: PropTypes.bool.isRequired,
  isAnnulable: PropTypes.bool.isRequired,
  isProcessingAnnul: PropTypes.bool.isRequired,
  onDebit: PropTypes.func.isRequired,
  onAnnul: PropTypes.func.isRequired,
  updateAutomaticDebitDate: PropTypes.func.isRequired,
}

export default OngoingPaymentDetailsUI
